/**
 * Airtable Retry Module
 * 
 * Implements retry strategies for handling transient errors with Airtable API
 * Includes exponential backoff, circuit breaker, and timeout handling
 */

import { AirtableError, AirtableErrorType } from './types';
import {
  AirtableApiError,
  AirtableRateLimitError,
  logAirtableError,
  isAirtableError,
  wrapError
} from './errors';

/**
 * Default retry configuration
 */
export interface RetryConfig {
  /** Maximum number of retry attempts */
  maxRetries: number;
  
  /** Initial delay in ms before first retry */
  initialDelayMs: number;
  
  /** Multiplier to increase delay each attempt */
  backoffFactor: number;
  
  /** Maximum delay between retries in ms */
  maxDelayMs: number;
  
  /** Types of errors that should trigger retries */
  retryableErrorTypes: AirtableErrorType[];
  
  /** Status codes that should trigger retries */
  retryableStatusCodes: number[];
  
  /** Whether to randomize delay to prevent thundering herd */
  jitter: boolean;
}

/**
 * Default retry configuration with reasonable values
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialDelayMs: 1000, // 1 second
  backoffFactor: 2,
  maxDelayMs: 30000, // 30 seconds
  retryableErrorTypes: [
    AirtableErrorType.NETWORK,
    AirtableErrorType.TIMEOUT,
    AirtableErrorType.RATE_LIMIT,
    AirtableErrorType.SERVER
  ],
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
  jitter: true
};

/**
 * Circuit breaker to prevent repeated calls to failing endpoints
 */
class CircuitBreaker {
  private static instance: CircuitBreaker;
  private failureCounters: Map<string, number> = new Map();
  private breakerStatus: Map<string, boolean> = new Map(); // true = open, false = closed
  private lastFailureTime: Map<string, number> = new Map();
  
  // Circuit breaker settings
  private failureThreshold = 5;
  private resetTimeoutMs = 60000; // 1 minute
  
  private constructor() {}
  
  public static getInstance(): CircuitBreaker {
    if (!CircuitBreaker.instance) {
      CircuitBreaker.instance = new CircuitBreaker();
    }
    return CircuitBreaker.instance;
  }
  
  /**
   * Record a failure for an endpoint
   */
  public recordFailure(endpoint: string): void {
    const currentCount = this.failureCounters.get(endpoint) || 0;
    const newCount = currentCount + 1;
    this.failureCounters.set(endpoint, newCount);
    this.lastFailureTime.set(endpoint, Date.now());
    
    // If we hit the threshold, open the circuit
    if (newCount >= this.failureThreshold) {
      this.breakerStatus.set(endpoint, true); // Open circuit
      console.warn(`Circuit breaker opened for endpoint: ${endpoint}`);
    }
  }
  
  /**
   * Record a success for an endpoint, reset the failure counter
   */
  public recordSuccess(endpoint: string): void {
    this.failureCounters.set(endpoint, 0);
    this.breakerStatus.set(endpoint, false); // Close circuit
  }
  
  /**
   * Check if circuit is open (should prevent calls) for an endpoint
   */
  public isOpen(endpoint: string): boolean {
    const isOpen = this.breakerStatus.get(endpoint) || false;
    
    if (isOpen) {
      // Check if the reset timeout has passed
      const lastFailure = this.lastFailureTime.get(endpoint) || 0;
      const now = Date.now();
      
      if (now - lastFailure > this.resetTimeoutMs) {
        // Reset time has passed, allow one request through (half-open state)
        this.breakerStatus.set(endpoint, false); // Close circuit
        return false;
      }
    }
    
    return isOpen;
  }
  
  /**
   * Update circuit breaker settings
   */
  public updateSettings(failureThreshold: number, resetTimeoutMs: number): void {
    this.failureThreshold = failureThreshold;
    this.resetTimeoutMs = resetTimeoutMs;
  }
}

/**
 * Check if an error should be retried based on configuration
 */
function isRetryableError(error: unknown, config: RetryConfig): boolean {
  // If it's an AirtableApiError, check status code
  if (error instanceof AirtableApiError) {
    if (config.retryableStatusCodes.includes(error.status)) {
      return true;
    }
    
    // Special handling for rate limit errors
    if (error.isRateLimitError()) {
      return true;
    }
  }
  
  // If it's an AirtableError, check type
  if (isAirtableError(error) && config.retryableErrorTypes.includes(error.type)) {
    return true;
  }
  
  // Generic network errors should be retried
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }
  
  return false;
}

/**
 * Calculate delay for next retry attempt with exponential backoff
 */
function calculateBackoffDelay(attempt: number, config: RetryConfig): number {
  const delay = Math.min(
    config.initialDelayMs * Math.pow(config.backoffFactor, attempt),
    config.maxDelayMs
  );
  
  // Add jitter to prevent thundering herd problem
  if (config.jitter) {
    // Random value between 0.8 and 1.2 (±20%)
    const jitterFactor = 0.8 + Math.random() * 0.4;
    return Math.floor(delay * jitterFactor);
  }
  
  return delay;
}

/**
 * Wait for specified milliseconds
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Execute a function with retry logic
 * 
 * @param fn The function to execute
 * @param endpoint String identifier for the endpoint (used for circuit breaker)
 * @param config Retry configuration
 * @returns Result from the function
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  endpoint: string,
  customConfig?: Partial<RetryConfig>
): Promise<T> {
  const config = { ...DEFAULT_RETRY_CONFIG, ...(customConfig || {}) };
  const circuitBreaker = CircuitBreaker.getInstance();
  
  // Check if circuit is open for this endpoint
  if (circuitBreaker.isOpen(endpoint)) {
    throw new AirtableError(
      `Circuit breaker open for endpoint: ${endpoint}`,
      AirtableErrorType.SERVER,
      undefined,
      undefined,
      { endpoint }
    );
  }
  
  let lastError: unknown;
  
  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      // On success, record it for circuit breaker
      const result = await fn();
      circuitBreaker.recordSuccess(endpoint);
      return result;
    } catch (error) {
      lastError = error;
      
      // Convert to AirtableError for consistent handling
      const airtableError = wrapError(error, { attempt, endpoint });
      
      // If it's a rate limit error, respect the retry-after header if present
      if (airtableError instanceof AirtableApiError && airtableError.isRateLimitError()) {
        const retryAfter = airtableError.getRetryAfter();
        
        // If we have a retry-after, use that instead of our backoff
        if (retryAfter !== null) {
          // If this is the last attempt, don't wait but fail
          if (attempt === config.maxRetries) {
            break;
          }
          
          // Wait the specified time and retry
          await delay(retryAfter * 1000); // Convert to milliseconds
          continue;
        }
      }
      
      // Log the error with attempt information
      logAirtableError(airtableError, { 
        attempt, 
        endpoint,
        willRetry: attempt < config.maxRetries && isRetryableError(error, config)
      });
      
      // Determine if we should retry
      if (attempt < config.maxRetries && isRetryableError(error, config)) {
        // Calculate backoff delay
        const backoffMs = calculateBackoffDelay(attempt, config);
        await delay(backoffMs);
        continue;
      }
      
      // Record failure for circuit breaker
      circuitBreaker.recordFailure(endpoint);
      
      // If we're not retrying, rethrow
      throw airtableError;
    }
  }
  
  // If we get here, we've exhausted retries
  throw wrapError(lastError, { 
    endpoint,
    attemptsExhausted: true,
    maxRetries: config.maxRetries
  });
}

/**
 * Higher-order function to add retry capabilities to any function
 */
export function withRetryWrapper<T, Args extends any[]>(
  fn: (...args: Args) => Promise<T>,
  endpointFn: (...args: Args) => string,
  customConfig?: Partial<RetryConfig>
): (...args: Args) => Promise<T> {
  return async (...args: Args): Promise<T> => {
    const endpoint = endpointFn(...args);
    return withRetry(() => fn(...args), endpoint, customConfig);
  };
}

/**
 * Create retry-enabled versions of functions
 */
export function createRetryableFunction<T, Args extends any[]>(
  fn: (...args: Args) => Promise<T>,
  endpointFn: (...args: Args) => string,
  customConfig?: Partial<RetryConfig>
): (...args: Args) => Promise<T> {
  return withRetryWrapper(fn, endpointFn, customConfig);
} 
